import React, { useState } from 'react';
import { 
  FileText, 
  Users, 
  Wallet, 
  CheckCircle2, 
  ArrowRight,
  Clock,
  Shield,
  Zap
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ContactModal } from '../components/ContactModal';
import { SEOHead } from '../components/SEOHead';

export default function AdminPage() {
  const [showContactModal, setShowContactModal] = useState(false);
  const { t } = useTranslation();

  return (
    <>
      <SEOHead 
        title="Rejoindre Communauté d'Énergie Bruxelles | Sun Is Up - Adhésion & Partage d'Énergie"
        description="Rejoignez la communauté d'énergie Bruxelles Sun Is Up en 4 étapes simples. Devenez membre pour bénéficier du partage d'énergie solaire et réduire votre facture électricité jusqu'à 30% en Belgique."
        keywords="rejoindre communauté d'énergie bruxelles, adhésion communauté énergie belgique, devenir membre partage énergie bruxelles, inscription communauté énergie bruxelles, join energy community brussels, become member energy sharing brussels, lid worden energiegemeenschap brussel, aansluiten energie delen brussel, comment rejoindre communauté énergie, réduction facture électricité belgique"
        url="https://sunisup.be/admin"
        logo="https://sunisup.be/images/logo.png"
      />
      <div className="min-h-screen bg-white font-sans">
      {/* How It Works Section */}
      <section className="py-20 lg:py-32" style={{ backgroundColor: '#ffffff' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 sm:mb-8 font-sans leading-tight" style={{ color: '#1a1a1a' }}>
              {t('howItWorks.title')}
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl max-w-5xl mx-auto font-sans leading-relaxed" style={{ color: '#4a4a4a' }}>
              {t('howItWorks.intro')}
            </p>
          </div>

          <div className="space-y-8 mb-16">
            {(t('howItWorks.paragraphs', { returnObjects: true }) as string[]).map((paragraph: string, index: number) => {
              if (paragraph === 'VIDEO_PARTAGE') {
                return (
                  <div key={index} className="max-w-5xl mx-auto">
                    <video
                      controls
                      className="w-full rounded-2xl shadow-lg"
                      style={{ maxHeight: '600px' }}
                    >
                      <source src="/videos/Partage.mp4" type="video/mp4" />
                      Votre navigateur ne supporte pas la lecture de vidéos.
                    </video>
                    <p className="text-center text-sm font-sans mt-4" style={{ color: '#666666' }}>
                      Fonctionnement d'une communauté expliqué par Brugel
                    </p>
                  </div>
                );
              }
              return (
                <p key={index} className="text-base sm:text-lg leading-relaxed max-w-5xl mx-auto font-sans" style={{ color: '#666666' }}>
                  {paragraph}
                </p>
              );
            })}
          </div>

          <div className="mt-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-10 text-center font-sans" style={{ color: '#1a1a1a' }}>
              {t('howItWorks.benefits.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {(t('howItWorks.benefits.items', { returnObjects: true }) as Array<{title: string, description: string}>).map((benefit, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300" style={{ border: '1px solid #e5e5e5' }}>
                  <div className="flex items-start mb-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4 flex-shrink-0" style={{ backgroundColor: '#FFF5E1' }}>
                      <CheckCircle2 className="w-6 h-6" style={{ color: '#F8BD3D' }} />
                    </div>
                    <h3 className="text-xl font-bold font-sans" style={{ color: '#1a1a1a' }}>
                      {benefit.title}
                    </h3>
                  </div>
                  <p className="text-base leading-relaxed font-sans" style={{ color: '#666666' }}>
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How To Join Section */}
      <section className="py-20 lg:py-32" style={{ background: 'linear-gradient(to bottom right, #FFF5E1, #ffffff)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-6 py-3 rounded-full font-medium shadow-lg mb-8 font-sans" style={{ backgroundColor: 'rgba(248, 189, 61, 0.15)', border: '1px solid rgba(248, 189, 61, 0.3)', color: '#F8BD3D' }}>
            <Users className="w-4 h-4 mr-2" />
            {t('howToJoin.badge')}
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 sm:mb-8 font-sans leading-tight" style={{ color: '#1a1a1a' }}>
            {t('howToJoin.title')}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl max-w-4xl mx-auto font-sans leading-relaxed px-2" style={{ color: '#4a4a4a' }}>
            {t('howToJoin.intro')}
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        {/* Steps Section */}
        <div className="space-y-8 sm:space-y-12">
          {/* Step 1 */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl p-6 sm:p-8 lg:p-12 hover:shadow-3xl transition-all duration-300" style={{ border: '1px solid #e5e5e5' }}>
            <div className="flex flex-col sm:flex-row items-start space-y-6 sm:space-y-0 sm:space-x-8">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto sm:mx-0" style={{ backgroundColor: '#F8BD3D' }}>
                  <FileText className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row items-center sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6 text-center sm:text-left">
                  <span className="font-bold px-3 py-1 sm:px-4 sm:py-2 rounded-full font-sans text-sm sm:text-base" style={{ backgroundColor: '#FFF5E1', color: '#F8BD3D' }}>
                    Étape 1
                  </span>
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold font-sans" style={{ color: '#1a1a1a' }}>{t('howToJoin.steps.documents.title')}</h3>
                </div>
                <ul className="space-y-3 sm:space-y-4">
                  {t('howToJoin.steps.documents.items', { returnObjects: true }).map((item: string, index: number) => (
                    <li key={index} className="flex items-start space-x-3 sm:space-x-4">
                      <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 mt-0.5 sm:mt-1" style={{ color: '#F8BD3D' }} />
                      <span className="text-base sm:text-lg font-sans" style={{ color: '#666666' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl p-6 sm:p-8 lg:p-12 hover:shadow-3xl transition-all duration-300" style={{ border: '1px solid #e5e5e5' }}>
            <div className="flex flex-col sm:flex-row items-start space-y-6 sm:space-y-0 sm:space-x-8">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto sm:mx-0" style={{ backgroundColor: '#F8BD3D' }}>
                  <Users className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row items-center sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6 text-center sm:text-left">
                  <span className="font-bold px-3 py-1 sm:px-4 sm:py-2 rounded-full font-sans text-sm sm:text-base" style={{ backgroundColor: '#FFF5E1', color: '#F8BD3D' }}>
                    Étape 2
                  </span>
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold font-sans" style={{ color: '#1a1a1a' }}>{t('howToJoin.steps.membership.title')}</h3>
                </div>
                <ul className="space-y-3 sm:space-y-4">
                  {t('howToJoin.steps.membership.items', { returnObjects: true }).map((item: string, index: number) => (
                    <li key={index} className="flex items-start space-x-3 sm:space-x-4">
                      <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 mt-0.5 sm:mt-1" style={{ color: '#F8BD3D' }} />
                      <span className="text-base sm:text-lg font-sans" style={{ color: '#666666' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl p-6 sm:p-8 lg:p-12 hover:shadow-3xl transition-all duration-300" style={{ border: '1px solid #e5e5e5' }}>
            <div className="flex flex-col sm:flex-row items-start space-y-6 sm:space-y-0 sm:space-x-8">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto sm:mx-0" style={{ backgroundColor: '#F8BD3D' }}>
                  <Wallet className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row items-center sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6 text-center sm:text-left">
                  <span className="font-bold px-3 py-1 sm:px-4 sm:py-2 rounded-full font-sans text-sm sm:text-base" style={{ backgroundColor: '#FFF5E1', color: '#F8BD3D' }}>
                    Étape 3
                  </span>
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold font-sans" style={{ color: '#1a1a1a' }}>{t('howToJoin.steps.financial.title')}</h3>
                </div>
                <ul className="space-y-3 sm:space-y-4">
                  {t('howToJoin.steps.financial.items', { returnObjects: true }).map((item: string, index: number) => (
                    <li key={index} className="flex items-start space-x-3 sm:space-x-4">
                      <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 mt-0.5 sm:mt-1" style={{ color: '#F8BD3D' }} />
                      <span className="text-base sm:text-lg font-sans" style={{ color: '#666666' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl p-6 sm:p-8 lg:p-12 hover:shadow-3xl transition-all duration-300" style={{ border: '1px solid #e5e5e5' }}>
            <div className="flex flex-col sm:flex-row items-start space-y-6 sm:space-y-0 sm:space-x-8">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto sm:mx-0" style={{ backgroundColor: '#F8BD3D' }}>
                  <Zap className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row items-center sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6 text-center sm:text-left">
                  <span className="font-bold px-3 py-1 sm:px-4 sm:py-2 rounded-full font-sans text-sm sm:text-base" style={{ backgroundColor: '#FFF5E1', color: '#F8BD3D' }}>
                    Étape 4
                  </span>
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold font-sans" style={{ color: '#1a1a1a' }}>{t('howToJoin.steps.activation.title')}</h3>
                </div>
                <ul className="space-y-3 sm:space-y-4">
                  {t('howToJoin.steps.activation.items', { returnObjects: true }).map((item: string, index: number) => (
                    <li key={index} className="flex items-start space-x-3 sm:space-x-4">
                      <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 mt-0.5 sm:mt-1" style={{ color: '#F8BD3D' }} />
                      <span className="text-base sm:text-lg font-sans" style={{ color: '#666666' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-12 sm:mt-16 lg:mt-20 rounded-2xl sm:rounded-3xl shadow-2xl p-8 sm:p-12 lg:p-16 text-center relative overflow-hidden" style={{ backgroundColor: '#FFF5E1' }}>
          <div className="relative">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 sm:mb-6 font-sans leading-tight" style={{ color: '#1a1a1a' }}>
            {t('howToJoin.cta.title')}
          </h3>
            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl mb-8 sm:mb-10 lg:mb-12 max-w-3xl mx-auto font-sans leading-relaxed px-2" style={{ color: '#4a4a4a' }}>
            {t('howToJoin.cta.description')}
          </p>

          <button
            onClick={() => setShowContactModal(true)}
              className="px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-full font-bold transition-all duration-200 shadow-2xl hover:shadow-3xl hover:scale-105 flex items-center gap-2 sm:gap-3 mx-auto text-base sm:text-lg font-sans"
              style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
          >
             {t('howToJoin.cta.title')}
              <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          </div>
        </div>
      </div>

      <ContactModal 
        isOpen={showContactModal} 
        onClose={() => setShowContactModal(false)} 
      />
      </div>
    </>
  );
}